<script setup>
import { ref, onMounted } from 'vue';
import Dialog from 'primevue/dialog';
import { useUserProfileStore } from '@/stores/userProfileStore';

const profileStore = useUserProfileStore();

// Props
const props = defineProps({
    modelValue: {
        type: Boolean,
        required: true
    }
});

// Emits
const emit = defineEmits(['update:modelValue']);

// Functions
const logout = () => {
    // This would typically handle the logout process
    emit('update:modelValue', false);
};

const handlePersonalDetails = () => {
    window.open('https://profile.atlas-apps.mit.edu', '_blank');
};

// Add global style to hide scrollbars when component is mounted
const style = document.createElement('style');
onMounted(() => {
    style.textContent = `
    .profile-modal-dialog .p-dialog-content {
      -ms-overflow-style: none !important;
      scrollbar-width: none !important;
    }
    .profile-modal-dialog .p-dialog-content::-webkit-scrollbar {
      display: none !important;
    }
  `;
    document.head.appendChild(style);
});
</script>

<template>
    <Dialog
        :visible="modelValue"
        @update:visible="$emit('update:modelValue', $event)"
        :dismissableMask="true"
        modal
        :style="{ width: '500px', borderRadius: '24px' }"
        :showHeader="false"
        :closable="false"
        class="profile-modal-dialog hide-scrollbar"
        :contentStyle="{ borderRadius: '24px', padding: '0', height: '100%' }"
        :pt="{
            root: { class: 'rounded-dialog hide-scrollbar' },
            content: { class: 'rounded-content hide-scrollbar p-0' }
        }"
        pt:mask:class="backdrop-blur-sm"
    >
        <div class="profile-modal-container">
            <!-- Sticky Header with back button and title -->
            <div class="sticky top-0 z-10 bg-white pl-3 pt-6 pr-6 pb-4 border-b border-gray-100">
                <div class="flex items-center">
                    <button class="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 text-black" @click="$emit('update:modelValue', false)">
                        <i class="pi pi-arrow-left"></i>
                    </button>
                    <h2 class="text-lg font-medium ml-2 text-black">Manage account</h2>
                </div>
            </div>

            <!-- Scrollable Content Area -->
            <div class="overflow-y-auto flex-1 px-6 pt-4 pb-6 hide-scrollbar">
                <!-- Profile section -->
                <div class="flex items-center mb-5">
                    <div class="relative">
                        <img :src="profileStore.pictureUrl" alt="Profile" class="w-16 h-16 rounded-full object-cover" />
                        <!-- <div class="absolute bottom-0 right-0 bg-black rounded-full w-6 h-6 flex items-center justify-center">
                            <img src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Icon%402x-01lKLXYSf5buoH5xGUAvUQqDIinUF0.png" alt="Camera" class="w-3 h-3" />
                        </div> -->
                    </div>
                    <div class="ml-3">
                        <h3 class="text-lg font-normal text-black">{{ profileStore.isLoading ? 'Loading...' : profileStore.userProfile?.fullName }}</h3>
                        <!-- <p class="text-black opacity-75 text-xs">She/Her</p> -->
                    </div>
                </div>

                <!-- Account sections -->
                <div class="space-y-4">
                    <!-- Personal details - full width -->
                    <div class="bg-white rounded-xl p-4 transition-colors cursor-pointer hover:bg-[rgba(47,82,121,0.05)]" @click="handlePersonalDetails">
                        <div class="flex flex-col items-start">
                            <div class="p-2 rounded-full bg-transparent flex items-center justify-center w-9 h-9 mb-2 self-start">
                                <img src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/personalDetails-yMnVYqy4Oz3FlCu1Wyp0zxXlQ0S3RH.svg" alt="Personal Details" class="w-5 h-5" />
                            </div>
                            <div>
                                <h4 class="font-medium text-sm mb-1 text-black">Personal details</h4>
                                <p class="text-xs text-black opacity-75">Personal info, demographic info, citizenship & immigration, and registration status</p>
                            </div>
                        </div>
                    </div>

                    <!-- 2x2 Grid for other options -->
                    <div class="grid grid-cols-2 gap-4">
                        <!-- Mobile ID & Access -->
                        <!-- <div class="bg-white rounded-xl p-4 transition-colors cursor-pointer hover:bg-[rgba(47,82,121,0.05)]">
                            <div class="flex flex-col items-start">
                                <div class="p-2 rounded-full bg-transparent flex items-center justify-center w-9 h-9 mb-2 self-start">
                                    <img src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/mobileis%26access-RD7pe4ySyyYT94M4xmEgfW01xUn9xY.svg" alt="Mobile ID & Access" class="w-5 h-5" />
                                </div>
                                <div>
                                    <h4 class="font-medium text-sm mb-1 text-black">Mobile ID & Access</h4>
                                    <p class="text-xs text-black opacity-75">Setup your mobile ID and physical card</p>
                                </div>
                            </div>
                        </div> -->

                        <!-- MIT ID Barcode -->
                        <!-- <div class="bg-white rounded-xl p-4 transition-colors cursor-pointer hover:bg-[rgba(47,82,121,0.05)]">
                            <div class="flex flex-col items-start">
                                <div class="p-2 rounded-full bg-transparent flex items-center justify-center w-9 h-9 mb-2 self-start">
                                    <img src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/mitidbarcode-sLU0SGpoV7melhGKMtddrS7XlLWjDT.svg" alt="MIT ID Barcode" class="w-5 h-5" />
                                </div>
                                <div>
                                    <h4 class="font-medium text-sm mb-1 text-black">MIT ID Barcode</h4>
                                    <p class="text-xs text-black opacity-75">Your unique barcode to quickly access events</p>
                                </div>
                            </div>
                        </div> -->

                        <!-- My PIN -->
                        <!-- <div class="bg-white rounded-xl p-4 transition-colors cursor-pointer hover:bg-[rgba(47,82,121,0.05)]">
                            <div class="flex flex-col items-start">
                                <div class="p-2 rounded-full bg-transparent flex items-center justify-center w-9 h-9 mb-2 self-start">
                                    <img src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/authentication-LsUANbrnWxGCYxOCrqXqOOf0v1mOOb.svg" alt="Authentication" class="w-5 h-5" />
                                </div>
                                <div>
                                    <h4 class="font-medium text-sm mb-1 text-black">My PIN</h4>
                                    <p class="text-xs text-black opacity-75">Authentication</p>
                                </div>
                            </div>
                        </div> -->

                        <!-- Security -->
                        <!-- <div class="bg-white rounded-xl p-4 transition-colors cursor-pointer hover:bg-[rgba(47,82,121,0.05)]">
                            <div class="flex flex-col items-start">
                                <div class="p-2 rounded-full bg-transparent flex items-center justify-center w-9 h-9 mb-2 self-start">
                                    <img src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/security-Fcy8c1KGditzuYd1AOrDy25zrnFq9r.svg" alt="Security" class="w-5 h-5" />
                                </div>
                                <div>
                                    <h4 class="font-medium text-sm mb-1 text-black">Security</h4>
                                    <p class="text-xs text-black opacity-75">Change password</p>
                                </div>
                            </div>
                        </div> -->
                    </div>
                </div>

                <!-- Log out button -->
                <div class="mt-5 flex justify-center">
                    <button class="flex items-center text-red-500 font-medium px-4 py-2 rounded-lg hover:bg-red-50 transition-colors text-sm" @click="logout">
                        <img src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/logouticon-RQdKYN1h2E7prmr35BEVneU9aA06z4.svg" alt="Log out" class="w-4 h-4 mr-2" />
                        Log out
                    </button>
                </div>
            </div>
        </div>
    </Dialog>
</template>

<style>
/* Global styles to ensure consistent styling with AppLauncher */
.profile-modal-dialog.p-dialog {
    border-radius: 24px !important;
    overflow: hidden;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.profile-modal-dialog .p-dialog-mask {
    backdrop-filter: blur(10px) !important;
    -webkit-backdrop-filter: blur(10px) !important;
    background-color: rgba(0, 0, 0, 0.6) !important;
    z-index: 9999 !important;
}

.profile-modal-dialog .p-dialog-content {
    padding: 0 !important;
    border-radius: 24px !important;
    max-height: 640px;
    -ms-overflow-style: none !important;
    scrollbar-width: none !important;
    display: flex;
    flex-direction: column;
}

.profile-modal-dialog .p-dialog-content::-webkit-scrollbar {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
}

.profile-modal-dialog .rounded-dialog {
    border-radius: 24px !important;
}

.profile-modal-dialog .rounded-content {
    border-radius: 24px !important;
}

.profile-modal-dialog .p-dialog-wrapper {
    border-radius: 24px !important;
}

.profile-modal-dialog .p-dialog-container {
    border-radius: 24px !important;
}
</style>

<style scoped>
.profile-modal-container {
    background-color: #fbfbfb;
    border-radius: 24px;
    width: 100%;
    height: 100%;
    max-height: 640px;
    overflow-y: auto;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
}

.hide-scrollbar {
    -ms-overflow-style: none !important;
    scrollbar-width: none !important;
}

.hide-scrollbar::-webkit-scrollbar {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
}

/* Custom hover effect with #2F5279 at 5% opacity */
.hover-bg-custom:hover {
    background-color: rgba(47, 82, 121, 0.05);
}
</style>
